import { notification } from 'antd';
import { ArgsProps } from 'antd/es/notification/interface';
import React, { createContext, useContext, ReactNode } from 'react';

import { trackError } from 'utils/analytics';
import { Logger } from 'utils/logger';

type NotificationAPI = ReturnType<typeof notification.useNotification>[0];

// Context
const NotificationContext = createContext<NotificationAPI | null>(null);

// Configuration
const NOTIFICATION_CONFIG = {
  placement: 'bottomRight' as const,
  showProgress: false,
  stack: { threshold: 2 },
};

// Provider Component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [api, contextHolder] =
    notification.useNotification(NOTIFICATION_CONFIG);

  return (
    <NotificationContext.Provider value={api}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

// Notification props - both title and description are required for proper centering
export interface NotificationProps extends ArgsProps {
  /** **Custom Prop**: 'filled' shows colored background (default), 'outlined' shows default styling */
  variant?: 'filled' | 'outlined';
}

// Hook
interface CustomNotificationAPI {
  success: (props: NotificationProps) => void;
  error: (props: NotificationProps & { error?: unknown }) => void;
  warning: (props: NotificationProps) => void;
  info: (props: NotificationProps) => void;
  destroy: NotificationAPI['destroy'];
  open: NotificationAPI['open'];
}

const toArgsProps = (props: NotificationProps): ArgsProps => {
  const { variant = 'filled', className, ...rest } = props;
  return {
    ...rest,
    className:
      variant === 'outlined'
        ? `notification-outlined ${className ?? ''}`.trim()
        : className,
  } as unknown as ArgsProps;
};

/** Sanitizes error for logging - strips PII, request/headers/body, limits stack trace */
const sanitizeError = (error: unknown): Record<string, unknown> | string => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (!(error instanceof Error)) return 'Non-Error object';

  const sanitized: Record<string, unknown> = {
    name: error.name,
    message: error.message,
    stack: error.stack?.split('\n').slice(0, 2).join('\n'),
  };

  // Strip sensitive fields that may exist on axios/fetch errors
  const rawError = error as unknown as Record<string, unknown>;
  if ('code' in rawError) sanitized.code = rawError.code;
  if ('status' in rawError) sanitized.status = rawError.status;

  return sanitized;
};

// No-op implementation for when context is unavailable (e.g., during HMR)
const noopApi: CustomNotificationAPI = {
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
  destroy: () => {},
  open: () => {},
};

export const useNotification = (): CustomNotificationAPI => {
  const api = useContext(NotificationContext);

  // Return no-op during HMR or if context is not available
  if (!api) {
    if (import.meta.env.DEV) {
      return noopApi;
    }
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }

  return {
    success: (props) => api.success(toArgsProps(props)),
    info: (props) => api.info(toArgsProps(props)),
    warning: (props) => api.warning(toArgsProps(props)),
    destroy: api.destroy,
    open: api.open,
    error: ({ error, ...props }) => {
      // Log sanitized error to avoid leaking PII - detailed in DEV, minimal in production
      if (error) {
        if (import.meta.env.DEV) {
          Logger.error('Notification error:', sanitizeError(error));
        } else {
          Logger.error(
            'Notification error:',
            error instanceof Error ? error.name : 'Error'
          );
        }
      }

      trackError({
        message: error instanceof Error ? error.message : undefined,
        error,
        context: {
          title: typeof props.title === 'string' ? props.title : undefined,
          description:
            typeof props.description === 'string'
              ? props.description
              : undefined,
        },
      });

      api.error(toArgsProps(props));
    },
  };
};
